/**
 * Type-safe environment variable accessor
 * Provides validated access to environment variables with type safety and default values
 */

/**
 * Get a string environment variable with type-safe access
 * @param value - Environment variable value
 * @param defaultValue - Default value if variable is not set or invalid
 * @returns The environment variable value or default value
 */
function getEnvString(value: unknown, defaultValue: string): string {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return defaultValue;
}

/**
 * Get an optional string environment variable
 * @param value - Environment variable value
 * @returns The environment variable value or undefined if not set
 */
function getEnvStringOptional(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return undefined;
}

/**
 * Safely access import.meta.env, handling cases where it's undefined (e.g., in Node.js/Playwright)
 */
function getEnvVar(key: string): unknown {
  if (import.meta.env) {
    return import.meta.env[key];
  }
  return undefined;
}

/**
 * Application-specific environment variables
 */
export const env = {
  /**
   * Application version (set at build time from package.json)
   */
  get appVersion(): string {
    return getEnvString(getEnvVar('VITE_APP_VERSION'), '0.0.0');
  },

  /**
   * Supabase URL for authentication
   */
  get supabaseUrl(): string | undefined {
    return getEnvStringOptional(getEnvVar('VITE_SUPABASE_URL'));
  },

  /**
   * Supabase anonymous key for authentication
   */
  get supabaseAnonKey(): string | undefined {
    return getEnvStringOptional(getEnvVar('VITE_SUPABASE_ANON_KEY'));
  },

  /**
   * Sentry DSN for error tracking
   */
  get sentryDsn(): string | undefined {
    return getEnvStringOptional(getEnvVar('VITE_SENTRY_DSN'));
  },

  /**
   * Whether the app is in production mode
   */
  get isProd(): boolean {
    return getEnvVar('PROD') === true;
  },

  /**
   * Whether the app is in development mode
   */
  get isDev(): boolean {
    return getEnvVar('DEV') === true;
  },
};
